import { Component, ElementRef, OnInit, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';

import { AlertService, UIService } from 'src/app/_services';
import { CountService } from 'src/app/_services/count.service';
import { DeletedEntity, DeletedRecord, DeletedService } from 'src/app/_services/deleted.service';
import { contextOf, deletedAgo, impactLines } from './deleted.utils';

const KINDS: { entity: DeletedEntity, one: string, many: string }[] = [
  { entity: 'partners', one: 'Partner', many: 'Partners' },
  { entity: 'customers', one: 'Customer', many: 'Customers' },
  { entity: 'users', one: 'User', many: 'Users' },
  { entity: 'projects', one: 'Project', many: 'Projects' },
  { entity: 'tags', one: 'Tag', many: 'Tags' },
];

type Tab = 'all' | DeletedEntity;

interface Row {
  entity: DeletedEntity;
  record: DeletedRecord;
  kind: string;
  name: string;
  context: string;
  /** a deleted partner's or customer's users can't sign in */
  locksUsers: boolean;
  ago: string;
  /** what the search box matches, lower case */
  haystack: string;
}

interface Confirming {
  row: Row;
  lines: string[];
  loading: boolean;
  failed: boolean;
  typed: string;
}

/** Superadmin page: soft-deleted records, to restore or delete permanently */
@Component({
    selector: 'app-deleted',
    templateUrl: './deleted.component.html',
    styleUrls: ['./deleted.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeletedComponent implements OnInit {
  readonly tabs: { id: Tab, label: string }[] = [{ id: 'all', label: 'All' }, ...KINDS.map(k => ({ id: k.entity, label: k.many }))];
  tab: Tab = 'all';
  search = '';
  rows: Row[] = [];
  loading = true;
  failed = false;
  confirming: Confirming | null = null;

  /** focus the name field as the dialog opens */
  @ViewChild('confirmName') set confirmName(input: ElementRef<HTMLInputElement> | undefined) {
    input?.nativeElement.focus();
  }

  constructor(
    private deletedService: DeletedService,
    private countService: CountService,
    private alert: AlertService,
    private uiservice: UIService,
  ) { }

  ngOnInit() {
    this.uiservice.setTitle('Deleted Records');
    forkJoin(KINDS.map(k => this.deletedService.list(k.entity))).subscribe({
      next: lists => {
        const now = new Date();
        this.rows = lists
          .flatMap(({ records }, i) => records.map(record => this.toRow(KINDS[i], record, now)))
          .sort((a, b) => b.record.deletedAt.localeCompare(a.record.deletedAt));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.failed = true;
      },
    });
  }

  count(tab: Tab) {
    return tab === 'all' ? this.rows.length : this.rows.filter(row => row.entity === tab).length;
  }

  get visible() {
    const query = this.search.trim().toLowerCase();
    return this.rows.filter(row =>
      (this.tab === 'all' || row.entity === this.tab) && (!query || row.haystack.includes(query)));
  }

  /** The empty state's title and line for the current tab */
  get empty() {
    const kind = KINDS.find(k => k.entity === this.tab);
    return kind
      ? { title: `No Deleted ${kind.many}`, text: `When a ${kind.one.toLowerCase()} is deleted, it shows up here so you can restore it or delete it permanently.` }
      : { title: 'No Deleted Records', text: 'Deleted partners, customers, users, projects and tags show up here.' };
  }

  restore(row: Row) {
    this.deletedService.restore(row.entity, row.record.id).subscribe({
      next: () => {
        this.remove(row);
        this.alert.success(`${row.name} restored`);
      },
      error: ({ error }) => this.alert.error(error?.message || 'Something went wrong'),
    });
  }

  /** Open the permanent-delete dialog, counting what a partner or customer takes with it */
  askDelete(row: Row) {
    const confirming: Confirming = { row, lines: impactLines(row.entity, {}), loading: false, failed: false, typed: '' };
    this.confirming = confirming;
    if (row.entity !== 'partners' && row.entity !== 'customers') { return; }
    confirming.loading = true;
    this.deletedService.impact(row.entity, row.record.id).subscribe({
      next: ({ impact }) => {
        confirming.lines = impactLines(row.entity, impact);
        confirming.loading = false;
      },
      error: () => {
        confirming.failed = true;
        confirming.loading = false;
      },
    });
  }

  get canDelete() {
    const c = this.confirming;
    return !!c && !c.loading && c.typed.trim() === c.row.name;
  }

  deletePermanently() {
    const { row } = this.confirming;
    this.deletedService.hardDelete(row.entity, row.record.id).subscribe({
      next: () => {
        this.confirming = null;
        this.remove(row, true);
        this.alert.success(`${row.name} permanently deleted`);
      },
      error: ({ error }) => this.alert.error(error?.message || 'Something went wrong'),
    });
  }

  /** Take a record off the list; after a permanent delete, also the listed records it owned, which its cascade removed */
  private remove(row: Row, withOwned = false) {
    const gone = new Set([row.record.id]);
    for (let size = 0; withOwned && size !== gone.size;) {
      size = gone.size;
      for (const { record } of this.rows) {
        if ([record.partner, record.customer, record.project].some(id => id && gone.has(String(id)))) {
          gone.add(record.id);
        }
      }
    }
    this.rows = this.rows.filter(other => !gone.has(other.record.id));
    // the entity cards count restored records again
    this.countService.getCounts()?.subscribe({ error: () => undefined });
  }

  private toRow({ entity, one }: typeof KINDS[number], record: DeletedRecord, now: Date): Row {
    const name = entity === 'users' ? record.email : record.name;
    const context = contextOf(entity, record);
    return {
      entity,
      record,
      kind: one,
      name,
      context,
      locksUsers: entity === 'partners' || entity === 'customers',
      ago: deletedAgo(record.deletedAt, now),
      haystack: [name, context, record.deletedByEmail || record.deletedBy].join(' ').toLowerCase(),
    };
  }
}
